/**
 * DebugConsole — HTML/CSS overlay toggled by ~ key.
 * Shows command input + log output. Commands are extensible.
 */
import debugLog from './DebugLog.js';

class DebugConsole {
  constructor() {
    this._visible = false;
    this._commands = new Map();
    this._el = null;
    this._outputEl = null;
    this._inputEl = null;
    this._history = [];
    this._historyIndex = -1;
    this._initialized = false;
  }

  /**
   * Initialize the debug console DOM and built-in commands.
   */
  init() {
    if (this._initialized) return;
    this._initialized = true;

    this._createDOM();
    this._bindKeys();
    this._registerBuiltinCommands();

    debugLog.info('DebugConsole initialized');
  }

  _createDOM() {
    // Container
    this._el = document.createElement('div');
    this._el.id = 'debug-console';
    this._el.className = 'debug-console';
    this._el.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 40%;
      min-height: 200px;
      background: rgba(0, 0, 0, 0.92);
      color: #e0e0e0;
      font-family: 'Courier New', monospace;
      font-size: 13px;
      z-index: 9999;
      display: none;
      flex-direction: column;
      border-bottom: 2px solid #4fc3f7;
    `;

    // Header
    const header = document.createElement('div');
    header.style.cssText = `
      padding: 4px 10px;
      background: rgba(79, 195, 247, 0.15);
      color: #4fc3f7;
      font-weight: bold;
      font-size: 12px;
      border-bottom: 1px solid rgba(79, 195, 247, 0.3);
      flex-shrink: 0;
    `;
    header.textContent = 'Wanderlight Debug Console (~ to toggle, "help" for commands)';
    this._el.appendChild(header);

    // Output area
    this._outputEl = document.createElement('div');
    this._outputEl.className = 'debug-console-output';
    this._outputEl.style.cssText = `
      flex: 1;
      overflow-y: auto;
      padding: 6px 10px;
      white-space: pre-wrap;
      word-break: break-word;
    `;
    this._el.appendChild(this._outputEl);

    // Input row
    const inputRow = document.createElement('div');
    inputRow.style.cssText = `
      display: flex;
      border-top: 1px solid rgba(255,255,255,0.1);
      flex-shrink: 0;
    `;

    const prompt = document.createElement('span');
    prompt.style.cssText = `
      padding: 6px 8px;
      color: #4fc3f7;
      font-weight: bold;
      flex-shrink: 0;
    `;
    prompt.textContent = '>';
    inputRow.appendChild(prompt);

    this._inputEl = document.createElement('input');
    this._inputEl.type = 'text';
    this._inputEl.className = 'debug-console-input';
    this._inputEl.style.cssText = `
      flex: 1;
      background: transparent;
      border: none;
      color: #fff;
      font-family: 'Courier New', monospace;
      font-size: 13px;
      padding: 6px 4px;
      outline: none;
    `;
    this._inputEl.addEventListener('keydown', (e) => this._onInputKey(e));
    inputRow.appendChild(this._inputEl);

    this._el.appendChild(inputRow);
    document.body.appendChild(this._el);
  }

  _bindKeys() {
    window.addEventListener('keydown', (e) => {
      if (e.key === '`' || e.key === '~') {
        e.preventDefault();
        this.toggle();
      }
    });
  }

  _onInputKey(e) {
    if (e.key === 'Enter') {
      const cmd = this._inputEl.value.trim();
      if (cmd) {
        this._history.push(cmd);
        this._historyIndex = this._history.length;
        this._execute(cmd);
      }
      this._inputEl.value = '';
      e.preventDefault();
    } else if (e.key === 'ArrowUp') {
      if (this._historyIndex > 0) {
        this._historyIndex--;
        this._inputEl.value = this._history[this._historyIndex];
      }
      e.preventDefault();
    } else if (e.key === 'ArrowDown') {
      if (this._historyIndex < this._history.length - 1) {
        this._historyIndex++;
        this._inputEl.value = this._history[this._historyIndex];
      } else {
        this._historyIndex = this._history.length;
        this._inputEl.value = '';
      }
      e.preventDefault();
    } else if (e.key === 'Escape') {
      this.hide();
      e.preventDefault();
    }
    // Stop propagation so game input doesn't trigger
    e.stopPropagation();
  }

  _execute(input) {
    this._print(`> ${input}`, '#4fc3f7');

    const parts = input.split(/\s+/);
    const name = parts[0].toLowerCase();
    const args = parts.slice(1);

    const command = this._commands.get(name);
    if (!command) {
      this._print(`Unknown command: "${name}". Type "help" for available commands.`, '#ff5252');
      return;
    }

    try {
      const result = command.handler(args);
      if (result !== undefined && result !== null) {
        this._print(String(result));
      }
    } catch (err) {
      this._print(`Error: ${err.message}`, '#ff5252');
      debugLog.error(`DebugConsole command error [${name}]: ${err.message}`);
    }
  }

  /**
   * Print a message to the console output.
   * @param {string} text
   * @param {string} [color]
   */
  _print(text, color) {
    const line = document.createElement('div');
    line.textContent = text;
    if (color) {
      line.style.color = color;
    }
    this._outputEl.appendChild(line);
    this._outputEl.scrollTop = this._outputEl.scrollHeight;
  }

  /**
   * Register a command.
   * @param {string} name
   * @param {Function} handler - receives (args: string[]) => string|void
   * @param {string} helpText
   */
  registerCommand(name, handler, helpText) {
    this._commands.set(name.toLowerCase(), { handler, helpText: helpText || '' });
    // Also expose on window.__debug.commands
    if (window.__debug && window.__debug.commands) {
      window.__debug.commands[name.toLowerCase()] = handler;
    }
  }

  _registerBuiltinCommands() {
    this.registerCommand('help', () => {
      const lines = ['Available commands:'];
      for (const [name, cmd] of this._commands) {
        lines.push(`  ${name.padEnd(20)} ${cmd.helpText}`);
      }
      return lines.join('\n');
    }, 'Show all available commands');

    this.registerCommand('listparty', () => {
      return 'Party system not yet implemented.';
    }, 'Print party state');

    this.registerCommand('screenshot', () => {
      return 'Screenshot system not yet implemented.';
    }, 'Save a screenshot');

    this.registerCommand('gold', (args) => {
      const amount = parseInt(args[0], 10);
      if (isNaN(amount)) return 'Usage: gold <amount>';
      return `Gold system not yet implemented. Would add ${amount} gold.`;
    }, 'Add gold: gold <amount>');

    this.registerCommand('setlevel', (args) => {
      const level = parseInt(args[0], 10);
      if (isNaN(level)) return 'Usage: setlevel <n>';
      return `Level system not yet implemented. Would set level to ${level}.`;
    }, 'Set party level: setlevel <n>');

    this.registerCommand('teleport', (args) => {
      const region = args[0];
      if (!region) return 'Usage: teleport <region_id>';
      return `Teleport system not yet implemented. Would teleport to ${region}.`;
    }, 'Teleport to region: teleport <region_id>');

    this.registerCommand('startcombat', (args) => {
      const preset = args[0] || '4x4';
      return `Combat system not yet implemented. Would start "${preset}" combat.`;
    }, 'Start combat encounter: startcombat [preset]');

    this.registerCommand('spawn', (args) => {
      const enemyId = args[0];
      const count = parseInt(args[1], 10) || 1;
      if (!enemyId) return 'Usage: spawn <enemy_id> [count]';
      return `Spawn system not yet implemented. Would spawn ${count}x ${enemyId}.`;
    }, 'Spawn enemies: spawn <enemy_id> [count]');

    this.registerCommand('give', (args) => {
      const itemId = args[0];
      const qty = parseInt(args[1], 10) || 1;
      const char = args[2] || 'party';
      if (!itemId) return 'Usage: give <item_id> [qty] [char]';
      return `Item system not yet implemented. Would give ${qty}x ${itemId} to ${char}.`;
    }, 'Give item: give <item_id> [qty] [char]');

    this.registerCommand('completequest', (args) => {
      const questId = args[0];
      if (!questId) return 'Usage: completequest <quest_id>';
      return `Quest system not yet implemented. Would complete ${questId}.`;
    }, 'Complete quest: completequest <quest_id>');

    this.registerCommand('settime', (args) => {
      const hour = parseInt(args[0], 10);
      if (isNaN(hour)) return 'Usage: settime <hour>';
      return `Time system not yet implemented. Would set time to ${hour}:00.`;
    }, 'Set in-game time: settime <hour>');

    this.registerCommand('clear', () => {
      this._outputEl.innerHTML = '';
    }, 'Clear console output');

    this.registerCommand('logs', () => {
      const logs = debugLog.getLogs();
      const recent = logs.slice(-20);
      return recent.map((l) => `[${l.level.toUpperCase()}] ${l.msg}`).join('\n');
    }, 'Show recent debug logs');

    this.registerCommand('entertown', (args) => {
      const townId = args[0];
      if (!townId) return 'Usage: entertown <town_id>';
      // Dynamic import to avoid circular deps
      Promise.all([
        import('../systems/TownSystem.js'),
        import('../core/SceneManager.js'),
      ]).then(([townMod, sceneMod]) => {
        const townSystem = townMod.default;
        const sceneManager = sceneMod.default;
        const town = townSystem.getTown(townId);
        if (!town) {
          const allTowns = townSystem.getAllTowns().map(t => t.id).join(', ');
          debugLog.warn(`Town not found: ${townId}. Available: ${allTowns}`);
          return;
        }
        sceneManager.transition('Town', {
          townId,
          party: window.__debug?.party || null,
        });
      });
      return `Entering town: ${townId}`;
    }, 'Enter a town: entertown <town_id>');
  }

  /**
   * Toggle console visibility.
   */
  toggle() {
    if (this._visible) {
      this.hide();
    } else {
      this.show();
    }
  }

  show() {
    this._visible = true;
    this._el.style.display = 'flex';
    this._inputEl.focus();
    // Refresh log output
    this._refreshLogs();
  }

  hide() {
    this._visible = false;
    this._el.style.display = 'none';
    this._inputEl.blur();
  }

  _refreshLogs() {
    // Show recent logs in output if empty
    if (this._outputEl.children.length === 0) {
      const logs = debugLog.getLogs();
      const recent = logs.slice(-10);
      for (const entry of recent) {
        const colors = {
          error: '#ff5252',
          warn: '#ffd740',
          event: '#4fc3f7',
          info: '#b0b0b0',
        };
        this._print(`[${entry.level.toUpperCase()}] ${entry.msg}`, colors[entry.level] || '#e0e0e0');
      }
    }
  }

  /**
   * Whether the console is currently visible.
   * @returns {boolean}
   */
  isVisible() {
    return this._visible;
  }
}

const debugConsole = new DebugConsole();
export default debugConsole;
